import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import styled from "styled-components";
import DomainDetails from "types/domainDetails";
import SearchResult from "types/searchResult";
import {
  toTitleCase,
  hasText,
  toThemePath,
  USE_CASES,
  STRUCTURE_TYPE_DESCRIPTIONS,
  buildDomainSummary,
  getCategoryDisplayLabel,
} from "./domainPageUtils";

// ── Styled components ───────────────────────────────────────────────────────

const MetaCard = styled.div`
  border: solid 1px #e8e8ef;
  border-radius: 8px;
  margin-bottom: 28px;
  overflow: hidden;
`;

const MetaCardSection = styled.div`
  padding: 16px 20px;
  & + & {
    border-top: solid 1px #e8e8ef;
  }
`;

const MetaLine = styled.p`
  margin: 0;
  color: #111827;
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1.4;
`;

const StructureNote = styled.p`
  margin: 10px 0 0;
  font-size: 1rem;
  color: #374151;
  line-height: 1.65;
`;

const AboutSection = styled.section`
  margin-top: 0;
  margin-bottom: 0;
`;

const AboutTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 16px;
`;

const AboutParagraph = styled.p`
  color: #374151;
  line-height: 1.65;
  margin-bottom: 18px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
`;

const UseCaseList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: #374151;
  line-height: 1.7;
`;

const UseCaseItem = styled.li`
  font-size: 1rem;
`;

const PillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ThemeToken = styled.span`
  display: inline-block;
  padding: 4px 9px;
  border-radius: 999px;
  background: #f3fdf1;
  color: #075f00;
  font-size: 0.87rem;
  font-weight: 500;
  margin-right: 6px;
  margin-top: 6px;

  &:last-child {
    margin-right: 0;
  }
`;

const DomainPill = styled(Link)`
  display: inline-block;
  padding: 4px 9px;
  border-radius: 999px;
  background: #f3fdf1;
  color: #075f00;
  font-size: 0.87rem;
  font-weight: 500;
  text-decoration: none;
  &:link {
    color: #075f00;
  }
  &:visited {
    color: #075f00;
  }
  &:hover {
    background: #e4fae0;
    color: #075f00;
  }
`;

const PillSpinnerWrapper = styled.div`
  color: #6ba1f1;
  padding: 6px 0;
`;

// ── Component ───────────────────────────────────────────────────────────────

interface DomainBrandContentProps {
  domainDetails: DomainDetails;
  domain: string | undefined;
  alternativeDomainNames?: SearchResult[];
  gettingAlternativeDomainNames?: boolean;
}

const DomainBrandContent = ({
  domainDetails,
  domain,
  alternativeDomainNames,
  gettingAlternativeDomainNames,
}: DomainBrandContentProps) => {
  const metaLineParts = useMemo(() => {
    const parts: string[] = [];

    if (hasText(domainDetails.primaryCategory)) {
      parts.push(
        getCategoryDisplayLabel(
          domainDetails.primaryCategory as string,
          domainDetails.primaryCategoryConfidence,
        ),
      );
    }
    if (hasText(domainDetails.structureType)) {
      parts.push(toTitleCase(domainDetails.structureType as string));
    }
    if (hasText(domainDetails.pronounceability)) {
      parts.push(
        `${toTitleCase(domainDetails.pronounceability as string)} pronounceability`,
      );
    }

    return parts;
  }, [domainDetails]);

  const semanticThemes = useMemo(() => {
    if (!domainDetails.semanticTokens) return [];

    return Array.from(
      new Set(domainDetails.semanticTokens.filter((token) => hasText(token))),
    ).map((token) => {
      const value = token.trim();
      return { label: toTitleCase(value), futurePath: toThemePath(value) };
    });
  }, [domainDetails]);

  const aboutContent = useMemo(() => {
    const lines = buildDomainSummary(
      domainDetails,
      semanticThemes.map((theme) => theme.label),
    );
    if (lines.length === 0) return null;

    return {
      title: `About ${domainDetails.domain || domain || "this domain"}`,
      lines,
    };
  }, [domainDetails, domain, semanticThemes]);

  const useCaseList = useMemo(() => {
    const themes = semanticThemes.map((t) => t.label.toLowerCase());
    const raw =
      themes.length > 0
        ? themes
            .slice(0, 5)
            .map(
              (t) =>
                USE_CASES[t] ??
                `${t.charAt(0).toUpperCase()}${t.slice(1)} projects`,
            )
        : hasText(domainDetails.primaryCategory)
          ? (() => {
              const cat = (domainDetails.primaryCategory as string)
                .trim()
                .toLowerCase();
              return [
                USE_CASES[cat] ??
                  `${toTitleCase(domainDetails.primaryCategory as string)} tools`,
              ];
            })()
          : [];

    return Array.from(new Set(raw));
  }, [domainDetails, semanticThemes]);

  const structureNote = useMemo(() => {
    if (!hasText(domainDetails.structureType)) return null;
    const key = (domainDetails.structureType as string).trim().toLowerCase();
    return STRUCTURE_TYPE_DESCRIPTIONS[key] ?? null;
  }, [domainDetails]);

  return (
    <>
      {(metaLineParts.length > 0 ||
        structureNote ||
        aboutContent ||
        useCaseList.length > 0 ||
        semanticThemes.length > 0 ||
        gettingAlternativeDomainNames ||
        (alternativeDomainNames && alternativeDomainNames.length > 0)) && (
        <MetaCard>
          {(metaLineParts.length > 0 || structureNote) && (
            <MetaCardSection>
              {metaLineParts.length > 0 && (
                <MetaLine>{metaLineParts.join(" \u00B7 ")}</MetaLine>
              )}
              {structureNote && <StructureNote>{structureNote}</StructureNote>}
            </MetaCardSection>
          )}
          {aboutContent && (
            <MetaCardSection>
              <AboutSection>
                <AboutTitle>{aboutContent.title}</AboutTitle>
                {aboutContent.lines.map((line) => (
                  <AboutParagraph key={line}>{line}</AboutParagraph>
                ))}
              </AboutSection>
            </MetaCardSection>
          )}
          {useCaseList.length > 0 && (
            <MetaCardSection>
              <SectionTitle>Potential uses</SectionTitle>
              <UseCaseList>
                {useCaseList.map((item) => (
                  <UseCaseItem key={item}>{item}</UseCaseItem>
                ))}
              </UseCaseList>
            </MetaCardSection>
          )}
          {semanticThemes.length > 0 && (
            <MetaCardSection>
              <SectionTitle>Related themes</SectionTitle>
              <PillList>
                {semanticThemes.map((theme) => (
                  <ThemeToken
                    key={theme.futurePath}
                    data-future-href={theme.futurePath}
                  >
                    {theme.label}
                  </ThemeToken>
                ))}
              </PillList>
            </MetaCardSection>
          )}
          {(gettingAlternativeDomainNames ||
            (alternativeDomainNames && alternativeDomainNames.length > 0)) && (
            <MetaCardSection>
              <SectionTitle>Similar Available Domains</SectionTitle>
              {gettingAlternativeDomainNames && !alternativeDomainNames && (
                <PillSpinnerWrapper>
                  <Spinner
                    as="span"
                    animation="border"
                    role="status"
                    size="sm"
                    aria-hidden="true"
                  />
                </PillSpinnerWrapper>
              )}
              {alternativeDomainNames && alternativeDomainNames.length > 0 && (
                <PillList>
                  {alternativeDomainNames.map((a) => (
                    <DomainPill key={a.domain} to={`/domain/${a.domain}`}>
                      {a.domain}
                    </DomainPill>
                  ))}
                </PillList>
              )}
            </MetaCardSection>
          )}
        </MetaCard>
      )}
    </>
  );
};

export default memo(DomainBrandContent);
